import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { NotificationSubscriptionResponseDto, TimeseriesDto } from '@api/devices';
import { CURRENT_CLIENT_TEMPLATE_VAR } from '@core';
import { map, Observable } from 'rxjs';

import type { DeviceChart, DeviceData, NormalizedDeviceData } from '../models/devices';
import { DeviceStatusActions } from '../models/devices';

@Injectable({ providedIn: 'root' })
export class DeviceService {
  private http = inject(HttpClient);

  deleteDevice(device: NormalizedDeviceData | DeviceData) {
    return this.http.delete(
      `devices/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/devices/${device.id}`,
    );
  }

  getAllDevicesByClientId(clientId: string, params: Record<string, string | boolean> = {}): Observable<DeviceData[]> {

    return this.http
      .get<{ data: DeviceData[]; }>(`devices/v1/clients/${clientId}/devices`, { params })
      .pipe(map(response => response.data));
  }

  getDeviceDetailsByDeviceId(deviceId: string): Observable<DeviceData> {
    return this.http
      .get<{ data: DeviceData; }>(`devices/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/devices/${deviceId}`)
      .pipe(map(response => response.data));
  }

  addNewDevice(clientId: string, locationId: string, data: Partial<DeviceData>): Observable<DeviceData> {
    return this.http
      .post<{ data: DeviceData; }>(`devices/v1/clients/${clientId}/devices`, { ...data, locationId })
      .pipe(map(response => response.data));
  }

  updateDeviceData(options: {
    data: Partial<DeviceData>;
    deviceId: string;
    locationId: string;
  }) {
    const { data, deviceId, locationId } = options;

    const action = data.status
      ? this.http.post<{ data: DeviceData; }>(
        `devices/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/devices/${deviceId}/${DeviceStatusActions[data.status]}`,
        {},
      )
      : this.http.patch<{ data: DeviceData; }>(
        `devices/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/devices/${deviceId}/withAttributes`,
        { ...data, locationId },
      );

    return action.pipe(map(response => response.data));
  }

  getNotificationSubscription(deviceId: string): Observable<NotificationSubscriptionResponseDto> {
    return this.http
      .get<{ data: NotificationSubscriptionResponseDto; }>(
        `devices/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/devices/${deviceId}/notification-subscription`,
      )
      .pipe(map(response => response.data));
  }

  getChartsData(options: { data: TimeseriesDto; deviceId: string; }) {
    return this.http
      .post<DeviceChart>(
        `devices/v2/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/devices/${options.deviceId}/timeseries`,
        options.data,
      )
      .pipe(map((response) => response as DeviceChart));
  }
}
